/**
 * MobileActionButtons - Sprint and Crawl action buttons for mobile
 *
 * Provides touch-friendly action buttons with visual feedback
 * and haptic response for sprint (boost) and crawl (slow) actions.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { MobileSettings } from '../utils/MobileSettings';

/**
 * Button type
 */
export enum ActionButtonType {
  SPRINT = 'sprint',
  CRAWL = 'crawl',
}

/**
 * Button state
 */
export interface ActionButtonState {
  type: ActionButtonType;
  pressed: boolean;
}

/**
 * Button configuration
 */
export interface ActionButtonConfig {
  /** Button diameter */
  diameter: number;
  /** Spacing between buttons */
  spacing: number;
  /** Edge padding */
  edgePadding: number;
}

const DEFAULT_CONFIG: ActionButtonConfig = {
  diameter: 70,
  spacing: 15,
  edgePadding: 20,
};

/**
 * Color palette for buttons
 */
const BUTTON_COLORS = {
  sprint: {
    primary: 0x00ff00,      // Bright green
    glow: 0x00ff88,         // Green glow
    pressed: 0x88ff00,      // Yellow-green when pressed
    icon: 0xffffff,
    text: 0x00ff00,
  },
  crawl: {
    primary: 0x4488ff,      // Blue
    glow: 0x00ccff,         // Cyan glow
    pressed: 0x66aaff,      // Light blue when pressed
    icon: 0xffffff,
    text: 0x4488ff,
  },
  background: 0x1a1a2e,
};

/**
 * Single action button
 */
class ActionButton {
  public container: Container;
  public type: ActionButtonType;

  private _background: Graphics;
  private _icon: Graphics;
  private _glow: Graphics;
  private _label: Text;

  private _pressed: boolean = false;
  private _diameter: number;
  private _animTime: number = 0;
  private _pressScale: number = 1;

  private _onPress: (() => void) | null = null;
  private _onRelease: (() => void) | null = null;

  constructor(type: ActionButtonType, diameter: number) {
    this.type = type;
    this._diameter = diameter;

    this.container = new Container();
    this.container.label = `action-button-${type}`;

    // Create graphics layers
    this._glow = new Graphics();
    this._background = new Graphics();
    this._icon = new Graphics();

    // Create label
    const colors = type === ActionButtonType.SPRINT ? BUTTON_COLORS.sprint : BUTTON_COLORS.crawl;
    const labelText = type === ActionButtonType.SPRINT ? 'SPRINT' : 'CRAWL';

    this._label = new Text({
      text: labelText,
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial, sans-serif',
        fontSize: 10,
        fontWeight: 'bold',
        fill: colors.text,
        letterSpacing: 1,
      }),
    });
    this._label.anchor.set(0.5, 0);
    this._label.position.set(0, diameter / 2 + 8);

    this.container.addChild(this._glow);
    this.container.addChild(this._background);
    this.container.addChild(this._icon);
    this.container.addChild(this._label);

    // Draw initial state
    this._draw();
  }

  /**
   * Draw the button
   */
  private _draw(): void {
    const radius = this._diameter / 2;
    const colors = this.type === ActionButtonType.SPRINT ? BUTTON_COLORS.sprint : BUTTON_COLORS.crawl;
    const currentColor = this._pressed ? colors.pressed : colors.primary;

    // Draw glow
    this._glow.clear();
    const glowAlpha = this._pressed ? 0.5 : 0.2;
    this._glow.circle(0, 0, radius + 6);
    this._glow.fill({ color: colors.glow, alpha: glowAlpha });

    // Draw background
    this._background.clear();
    this._background.circle(0, 0, radius);
    this._background.fill({ color: BUTTON_COLORS.background, alpha: 0.8 });
    this._background.circle(0, 0, radius);
    this._background.stroke({ width: 3, color: currentColor, alpha: 0.9 });

    // Draw icon
    this._icon.clear();
    if (this.type === ActionButtonType.SPRINT) {
      this._drawSprintIcon(colors.icon);
    } else {
      this._drawCrawlIcon(colors.icon);
    }
  }

  /**
   * Draw sprint icon (lightning bolt)
   */
  private _drawSprintIcon(color: number): void {
    const g = this._icon;
    const size = this._diameter * 0.35;

    // Lightning bolt shape
    g.moveTo(size * 0.2, -size);
    g.lineTo(-size * 0.3, 0);
    g.lineTo(0, 0);
    g.lineTo(-size * 0.2, size);
    g.lineTo(size * 0.3, 0);
    g.lineTo(0, 0);
    g.closePath();
    g.fill({ color, alpha: 0.9 });
  }

  /**
   * Draw crawl icon (turtle/slow symbol)
   */
  private _drawCrawlIcon(color: number): void {
    const g = this._icon;
    const size = this._diameter * 0.25;

    // Slow/snail icon - horizontal lines suggesting slowness
    const lineY = [-size * 0.6, 0, size * 0.6];
    const lineWidths = [size * 0.6, size, size * 0.6];

    lineY.forEach((y, i) => {
      g.moveTo(-lineWidths[i]! / 2, y);
      g.lineTo(lineWidths[i]! / 2, y);
      g.stroke({ width: 3, color, alpha: 0.9 });
    });

    // Small dots at ends
    g.circle(-size * 0.5, 0, 3);
    g.fill({ color, alpha: 0.9 });
    g.circle(size * 0.5, 0, 3);
    g.fill({ color, alpha: 0.9 });
  }

  /**
   * Set pressed state
   */
  setPressed(pressed: boolean): void {
    if (this._pressed === pressed) return;

    this._pressed = pressed;
    this._draw();

    if (pressed) {
      MobileSettings.getInstance().hapticTap();
      if (this._onPress) this._onPress();
    } else {
      if (this._onRelease) this._onRelease();
    }
  }

  /**
   * Check if pressed
   */
  isPressed(): boolean {
    return this._pressed;
  }

  /**
   * Update animation
   */
  update(dt: number): void {
    this._animTime += dt;

    // Scale animation when pressed
    const targetScale = this._pressed ? 0.9 : 1;
    this._pressScale += (targetScale - this._pressScale) * 10 * dt;
    this.container.scale.set(this._pressScale);

    // Pulse animation when pressed
    if (this._pressed) {
      const pulse = Math.sin(this._animTime * 10) * 0.1 + 1;
      this._glow.scale.set(pulse);
    } else {
      this._glow.scale.set(1);
    }
  }

  /**
   * Check if point is inside button
   */
  containsPoint(x: number, y: number): boolean {
    const localX = x - this.container.x;
    const localY = y - this.container.y;
    const radius = this._diameter / 2;
    return (localX * localX + localY * localY) <= radius * radius;
  }

  /**
   * Set press callback
   */
  onPress(callback: () => void): void {
    this._onPress = callback;
  }

  /**
   * Set release callback
   */
  onRelease(callback: () => void): void {
    this._onRelease = callback;
  }

  /**
   * Set diameter
   */
  setDiameter(diameter: number): void {
    this._diameter = diameter;
    this._label.position.set(0, diameter / 2 + 8);
    this._draw();
  }

  /**
   * Destroy
   */
  destroy(): void {
    this._glow.destroy();
    this._background.destroy();
    this._icon.destroy();
    this._label.destroy();
    this.container.destroy({ children: true });
  }
}

/**
 * MobileActionButtons - Container for sprint and crawl buttons
 */
export class MobileActionButtons {
  /** Main container */
  public container: Container;

  /** Sprint button */
  private _sprintButton: ActionButton;

  /** Crawl button */
  private _crawlButton: ActionButton;

  /** Configuration */
  private _config: ActionButtonConfig;

  /** Screen dimensions */
  private _screenWidth: number = 0;
  private _screenHeight: number = 0;

  /** Touch identifiers for each button */
  private _sprintTouchId: number | null = null;
  private _crawlTouchId: number | null = null;

  /** Bound event handlers */
  private _boundHandlers: {
    touchStart: (e: TouchEvent) => void;
    touchMove: (e: TouchEvent) => void;
    touchEnd: (e: TouchEvent) => void;
  };

  /** State change callback */
  private _onStateChange: ((sprint: boolean, crawl: boolean) => void) | null = null;

  constructor(config: Partial<ActionButtonConfig> = {}) {
    const settings = MobileSettings.getInstance();
    const buttonSize = settings.getButtonPixelSize();

    this._config = {
      ...DEFAULT_CONFIG,
      diameter: buttonSize,
      ...config,
    };

    // Create container
    this.container = new Container();
    this.container.label = 'mobile-action-buttons';

    // Create buttons
    this._sprintButton = new ActionButton(ActionButtonType.SPRINT, this._config.diameter);
    this._crawlButton = new ActionButton(ActionButtonType.CRAWL, this._config.diameter);

    this.container.addChild(this._sprintButton.container);
    this.container.addChild(this._crawlButton.container);

    // Bind event handlers
    this._boundHandlers = {
      touchStart: this._handleTouchStart.bind(this),
      touchMove: this._handleTouchMove.bind(this),
      touchEnd: this._handleTouchEnd.bind(this),
    };

    // Listen for settings changes
    settings.addListener(() => this._applySettings());
  }

  /**
   * Apply current settings
   */
  private _applySettings(): void {
    const settings = MobileSettings.getInstance();
    const buttonSize = settings.getButtonPixelSize();

    this._config.diameter = buttonSize;
    this._sprintButton.setDiameter(buttonSize);
    this._crawlButton.setDiameter(buttonSize);

    // Re-layout buttons
    this._updateLayout();
  }

  /**
   * Update button layout
   */
  private _updateLayout(): void {
    const settings = MobileSettings.getInstance();
    const radius = this._config.diameter / 2;
    const spacing = this._config.spacing;
    const padding = this._config.edgePadding;

    // Buttons are stacked vertically
    // Sprint on top, Crawl below
    const totalHeight = this._config.diameter * 2 + spacing;
    const startY = -totalHeight / 2 + radius;

    this._sprintButton.container.position.set(0, startY);
    this._crawlButton.container.position.set(0, startY + this._config.diameter + spacing);

    // Position container based on handedness
    if (settings.isRightHanded) {
      // Buttons on right side
      this.container.position.set(
        this._screenWidth - padding - radius,
        this._screenHeight / 2
      );
    } else {
      // Buttons on left side
      this.container.position.set(
        padding + radius,
        this._screenHeight / 2
      );
    }
  }

  /**
   * Handle touch start
   */
  private _handleTouchStart(e: TouchEvent): void {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;
      const x = touch.clientX;
      const y = touch.clientY;

      // Convert to container-local coordinates
      const localX = x - this.container.x;
      const localY = y - this.container.y;

      // Check sprint button
      if (this._sprintTouchId === null) {
        const sprintX = localX - this._sprintButton.container.x;
        const sprintY = localY - this._sprintButton.container.y;
        const sprintRadius = this._config.diameter / 2;

        if (sprintX * sprintX + sprintY * sprintY <= sprintRadius * sprintRadius) {
          this._sprintTouchId = touch.identifier;
          this._sprintButton.setPressed(true);
          this._notifyStateChange();
          e.preventDefault();
          continue;
        }
      }

      // Check crawl button
      if (this._crawlTouchId === null) {
        const crawlX = localX - this._crawlButton.container.x;
        const crawlY = localY - this._crawlButton.container.y;
        const crawlRadius = this._config.diameter / 2;

        if (crawlX * crawlX + crawlY * crawlY <= crawlRadius * crawlRadius) {
          this._crawlTouchId = touch.identifier;
          this._crawlButton.setPressed(true);
          this._notifyStateChange();
          e.preventDefault();
          continue;
        }
      }
    }
  }

  /**
   * Handle touch move (for sliding onto/off buttons)
   */
  private _handleTouchMove(_e: TouchEvent): void {
    // We don't change button state on move - only on start/end
    // This prevents accidental activation while using joystick
  }

  /**
   * Handle touch end
   */
  private _handleTouchEnd(e: TouchEvent): void {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;

      if (touch.identifier === this._sprintTouchId) {
        this._sprintTouchId = null;
        this._sprintButton.setPressed(false);
        this._notifyStateChange();
        e.preventDefault();
      }

      if (touch.identifier === this._crawlTouchId) {
        this._crawlTouchId = null;
        this._crawlButton.setPressed(false);
        this._notifyStateChange();
        e.preventDefault();
      }
    }
  }

  /**
   * Notify state change
   */
  private _notifyStateChange(): void {
    if (this._onStateChange) {
      this._onStateChange(
        this._sprintButton.isPressed(),
        this._crawlButton.isPressed()
      );
    }
  }

  /**
   * Start listening for touch events
   */
  start(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('touchstart', this._boundHandlers.touchStart, { passive: false });
    canvas.addEventListener('touchmove', this._boundHandlers.touchMove, { passive: false });
    canvas.addEventListener('touchend', this._boundHandlers.touchEnd, { passive: false });
    canvas.addEventListener('touchcancel', this._boundHandlers.touchEnd, { passive: false });
  }

  /**
   * Stop listening for touch events
   */
  stop(canvas: HTMLCanvasElement): void {
    canvas.removeEventListener('touchstart', this._boundHandlers.touchStart);
    canvas.removeEventListener('touchmove', this._boundHandlers.touchMove);
    canvas.removeEventListener('touchend', this._boundHandlers.touchEnd);
    canvas.removeEventListener('touchcancel', this._boundHandlers.touchEnd);

    // Reset state
    this._sprintTouchId = null;
    this._crawlTouchId = null;
    this._sprintButton.setPressed(false);
    this._crawlButton.setPressed(false);
  }

  /**
   * Set state change callback
   */
  onStateChange(callback: (sprint: boolean, crawl: boolean) => void): void {
    this._onStateChange = callback;
  }

  /**
   * Check if sprinting
   */
  isSprinting(): boolean {
    return this._sprintButton.isPressed();
  }

  /**
   * Check if crawling
   */
  isCrawling(): boolean {
    return this._crawlButton.isPressed();
  }

  /**
   * Update animation
   */
  update(dt: number): void {
    this._sprintButton.update(dt);
    this._crawlButton.update(dt);
  }

  /**
   * Handle screen resize
   */
  onResize(width: number, height: number): void {
    this._screenWidth = width;
    this._screenHeight = height;
    this._updateLayout();
  }

  /**
   * Show/hide buttons
   */
  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  /**
   * Set sprint button callback
   */
  onSprintPress(callback: () => void): void {
    this._sprintButton.onPress(callback);
  }

  /**
   * Set sprint release callback
   */
  onSprintRelease(callback: () => void): void {
    this._sprintButton.onRelease(callback);
  }

  /**
   * Set crawl button callback
   */
  onCrawlPress(callback: () => void): void {
    this._crawlButton.onPress(callback);
  }

  /**
   * Set crawl release callback
   */
  onCrawlRelease(callback: () => void): void {
    this._crawlButton.onRelease(callback);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this._sprintButton.destroy();
    this._crawlButton.destroy();
    this.container.destroy({ children: true });
    this._onStateChange = null;
  }
}
